// Copyright (c) Ping. All rights reserved.
//
// Global call-signaling feed. Taps the SDK's `client.onMessage` (the same
// stream that drives chat) and routes any call signaling envelope into the
// call store. Mounted once, app-wide, so an incoming call rings regardless of
// which screen is open. Mirror of the chat receive path in
// features/chat/useIncomingMessages.tsx, but for call AppEvents only.

import { type CallSignalContext,parsedToCallSignal, parseEnvelope } from '@ping/ui';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import { log } from '@core/log/log';

import { useCallStore } from './store/callStore';

interface IncomingLike {
  conversation_id?: unknown;
  conversationId?: unknown;
  sender_device?: unknown;
  senderDevice?: unknown;
  plaintext?: unknown;
  hlc?: { wall_ms?: unknown; logical?: unknown };
  epoch?: unknown;
  content_hash?: unknown;
  contentHash?: unknown;
}

function toBytes(v: unknown): Uint8Array {
  if (v instanceof Uint8Array) return v;
  if (Array.isArray(v)) return Uint8Array.from(v as number[]);
  if (v && typeof v === 'object' && 'length' in (v as ArrayLike<number>)) {
    return Uint8Array.from(v as ArrayLike<number>);
  }
  return new Uint8Array(0);
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i += 1) out += (b[i] ?? 0).toString(16).padStart(2, '0');
  return out;
}

function num(v: unknown): number {
  return typeof v === 'bigint' ? Number(v) : typeof v === 'number' ? v : 0;
}

// Single active subscription guard — installing again tears the previous one
// down first, so repeated auth changes can never accumulate listeners.
let activeUnsub: (() => void) | null = null;

/**
 * Subscribe the call store to the SDK message stream. Returns an unsubscribe.
 * Idempotent: a prior subscription is removed before a new one is created.
 * Returns a no-op if there's no authenticated client.
 */
export function installCallSignalingFeed(): () => void {
  if (activeUnsub) {
    activeUnsub();
    activeUnsub = null;
  }
  const status = useSessionStore.getState().status;
  if (status.kind !== 'authenticated' || status.client === null) {
    return () => undefined;
  }
  const client = status.client as unknown as {
    onMessage(handler: (m: unknown) => void): () => void;
  };
  if (typeof client.onMessage !== 'function') return () => undefined;

  const unsubscribe = client.onMessage((raw: unknown) => {
    try {
      const m = raw as IncomingLike;
      const convBytes = toBytes(m.conversation_id ?? m.conversationId);
      const plaintext = toBytes(m.plaintext);
      if (plaintext.length === 0) return;

      const parsed = parseEnvelope(plaintext);
      // Only call signaling kinds map to a signal; everything else → null.
      const senderHex = bytesToHex(toBytes(m.sender_device ?? m.senderDevice));
      const wall = num(m.hlc?.wall_ms);
      const logical = num(m.hlc?.logical);
      // The call_invite's content hash is the in-call thread root (same on all
      // sides — content-addressed), so the callee can thread replies under it.
      const chBytes = toBytes(m.content_hash ?? m.contentHash);
      const ctx: CallSignalContext = {
        from: senderHex,
        fromDevice: senderHex,
        conversationId: bytesToHex(convBytes),
        // No server seq on IncomingMessage; HLC is the CAS order source.
        // wall_ms dominates; logical breaks same-ms ties. calls-drift §6.
        serverSeq: wall * 1_000 + Math.min(logical, 999),
        at: wall || Date.now(),
        isSelf: false,
        ...(chBytes.length > 0 ? { contentHash: bytesToHex(chBytes) } : {}),
      };
      const evt = parsedToCallSignal(parsed, ctx);
      if (evt) useCallStore.getState().ingestSignal(evt);
    } catch (err) {
      log.warn({ event: 'call.signal.ingest.failed', err: String(err) });
    }
  });

  activeUnsub = () => {
    unsubscribe();
    activeUnsub = null;
  };
  return activeUnsub;
}
